// src/components/RequireOnboarding.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";

const RequireOnboarding = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { companyId } = useCompany();

  useEffect(() => {
    let cancelled = false;

    const checkOnboarding = async () => {
      try {
        // 1) Must be logged in
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user) {
          if (!cancelled) navigate("/login", { replace: true });
          return;
        }

        // If no companyId yet, let CompanyContext resolve; but avoid infinite loading
        // (If user truly has no company membership, your app should route them to setup)
        if (!companyId) {
          // fallback: try membership quickly
          const { data: membership } = await supabase
            .from("company_members")
            .select("company_id")
            .eq("user_id", user.id)
            .maybeSingle();

          const resolvedCompanyId = membership?.company_id || null;

          if (!resolvedCompanyId) {
            // no company — likely needs setup
            if (!cancelled) {
              setLoading(false);
            }
            return;
          }

          // companyId in context will update soon; continue check using resolvedCompanyId
          const { data: co, error: coErr } = await supabase
            .from("company_onboarding")
            .select("completed")
            .eq("company_id", resolvedCompanyId)
            .maybeSingle();

          if (!coErr && co?.completed) {
            if (!cancelled) setLoading(false);
            return;
          }

          // fallback to profile
          const { data: profile } = await supabase
            .from("profiles")
            .select("has_onboarded")
            .eq("id", user.id)
            .maybeSingle();

          if (profile?.has_onboarded) {
            // best effort: mark company as completed to stop future member onboarding
            try {
              await supabase.from("company_onboarding").upsert(
                [
                  {
                    company_id: resolvedCompanyId,
                    completed: true,
                    completed_at: new Date().toISOString(),
                    completed_by: user.id,
                    updated_at: new Date().toISOString(),
                  },
                ],
                { onConflict: "company_id" }
              );
            } catch (e) {
              // ignore
            }

            if (!cancelled) setLoading(false);
            return;
          }

          if (!cancelled) navigate("/Onboarding", { replace: true });
          return;
        }

        // 2) Company-wide onboarding check
        const { data: co, error: coErr } = await supabase
          .from("company_onboarding")
          .select("completed")
          .eq("company_id", companyId)
          .maybeSingle();

        if (!coErr && co?.completed) {
          if (!cancelled) setLoading(false);
          return;
        }

        // 3) Legacy fallback: profile-based onboarding
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("has_onboarded")
          .eq("id", user.id)
          .maybeSingle();

        if (profileError) console.error("Profile check error:", profileError);

        if (profile?.has_onboarded) {
          // best effort: create company onboarding so other members don't see onboarding
          try {
            await supabase.from("company_onboarding").upsert(
              [
                {
                  company_id: companyId,
                  completed: true,
                  completed_at: new Date().toISOString(),
                  completed_by: user.id,
                  updated_at: new Date().toISOString(),
                },
              ],
              { onConflict: "company_id" }
            );
          } catch (e) {
            // ignore
          }

          if (!cancelled) setLoading(false);
          return;
        }

        // 4) Not onboarded company-wide → go onboarding
        if (!cancelled) navigate("/Onboarding", { replace: true });
      } catch (e) {
        console.error("RequireOnboarding error:", e);
        if (!cancelled) setLoading(false);
      }
    };

    checkOnboarding();

    return () => {
      cancelled = true;
    };
  }, [navigate, companyId]);

  if (loading) return <p>Loading…</p>;
  return children;
};

export default RequireOnboarding;
